import React, { useCallback, useEffect, useState } from 'react';
import axiosInstance from '../../utils/axiosConfig';
import { useAuth } from '../../contexts/AuthContext';
import { GUN_ADLARI, hataMesaji, tarihOku, tarihStr, randevuDegisti } from './randevuOrtak';
import { randevuTanimlariGetir } from './useRandevuTanimlar';
import '../takip/takip.css';
import './randevu.css';

// Personelin randevu günleri / saatleri, molası, izin ve bloke saatleri, online randevu bağlantısı.
// Ayarlar > Kullanıcı Yönetimi (yönetici), Profil (personelin kendisi) ve Randevular > Ekip ekranında kullanılır.

interface GunSatiri { acik: boolean; baslangic: string; bitis: string; molaVar: boolean; molaBaslangic: string; molaBitis: string; }
interface Bloke { blokeID: number; baslangic: string; bitis: string; tur: string; aciklama?: string | null; }

const GUN_SIRASI = [1, 2, 3, 4, 5, 6, 0];
const bosGun = (): GunSatiri => ({ acik: false, baslangic: '09:00', bitis: '18:00', molaVar: false, molaBaslangic: '12:30', molaBitis: '13:30' });

interface Props { kullaniciID: number; onKaydedildi?: () => void; }

const MusaitlikAyarlari: React.FC<Props> = ({ kullaniciID, onKaydedildi }) => {
    const { user } = useAuth();
    const kendisi = user?.kullaniciID === kullaniciID;
    const [yukleniyor, setYukleniyor] = useState(true);
    const [kaydediliyor, setKaydediliyor] = useState(false);
    const [mesaj, setMesaj] = useState<{ tur: 'ok' | 'hata'; metin: string } | null>(null);
    const [randevuAlabilir, setRandevuAlabilir] = useState(true);
    const [onlineAcik, setOnlineAcik] = useState(false);
    const [aralik, setAralik] = useState(30);
    const [gunler, setGunler] = useState<Record<number, GunSatiri>>({});
    const [blokeler, setBlokeler] = useState<Bloke[]>([]);
    const [onlineToken, setOnlineToken] = useState('');
    const [sistemOnline, setSistemOnline] = useState(false);
    const [guncelleme, setGuncelleme] = useState<string | null>(null);
    // Bloke formu
    const [blokeTur, setBlokeTur] = useState<'IZIN' | 'BLOKE'>('IZIN');
    const [bTarih1, setBTarih1] = useState(tarihStr(new Date()));
    const [bTarih2, setBTarih2] = useState(tarihStr(new Date()));
    const [bSaat1, setBSaat1] = useState('12:00');
    const [bSaat2, setBSaat2] = useState('13:00');
    const [bAciklama, setBAciklama] = useState('');

    const yukle = useCallback(async () => {
        setYukleniyor(true);
        try {
            const r = await axiosInstance.get(`/api/Randevular/musaitlik/${kullaniciID}`);
            const d = r.data;
            setRandevuAlabilir(d.randevuAlabilir);
            setOnlineAcik(d.onlineRandevuAcik);
            setAralik(d.aralikDk);
            setOnlineToken(d.onlineToken);
            setSistemOnline(d.onlineRandevuSistemAktif);
            setGuncelleme(d.guncellemeTarihi ? `${tarihOku(d.guncellemeTarihi).toLocaleString('tr-TR')}${d.guncelleyenAdi ? ` · ${d.guncelleyenAdi}` : ''}` : null);
            const g: Record<number, GunSatiri> = {};
            for (const x of [0, 1, 2, 3, 4, 5, 6]) g[x] = bosGun();
            for (const m of d.gunler as any[]) g[m.gun] = {
                acik: true, baslangic: m.baslangicSaati, bitis: m.bitisSaati,
                molaVar: !!m.molaBaslangic, molaBaslangic: m.molaBaslangic ?? '12:30', molaBitis: m.molaBitis ?? '13:30',
            };
            setGunler(g);
            setBlokeler(d.blokeler);
        } catch (e) { setMesaj({ tur: 'hata', metin: hataMesaji(e, 'Müsaitlik yüklenemedi.') }); }
        finally { setYukleniyor(false); }
    }, [kullaniciID]);
    useEffect(() => { yukle(); }, [yukle]);

    const gunGuncelle = (g: number, alan: Partial<GunSatiri>) => setGunler(prev => ({ ...prev, [g]: { ...prev[g], ...alan } }));
    const haftaIci = () => setGunler(prev => {
        const y = { ...prev };
        for (const g of [1, 2, 3, 4, 5]) y[g] = { acik: true, baslangic: '09:00', bitis: '18:00', molaVar: true, molaBaslangic: '12:30', molaBitis: '13:30' };
        y[6] = { ...y[6], acik: false }; y[0] = { ...y[0], acik: false };
        return y;
    });
    const ilkGunuKopyala = () => setGunler(prev => {
        const kaynak = GUN_SIRASI.map(g => prev[g]).find(x => x.acik);
        if (!kaynak) return prev;
        const y = { ...prev };
        for (const g of GUN_SIRASI) if (y[g].acik) y[g] = { ...kaynak };
        return y;
    });

    const kaydet = async () => {
        setKaydediliyor(true); setMesaj(null);
        try {
            await axiosInstance.post(`/api/Randevular/musaitlik/${kullaniciID}`, {
                randevuAlabilir, onlineRandevuAcik: onlineAcik, aralikDk: aralik,
                gunler: GUN_SIRASI.filter(g => gunler[g]?.acik).map(g => ({
                    gun: g, baslangicSaati: gunler[g].baslangic, bitisSaati: gunler[g].bitis,
                    molaBaslangic: gunler[g].molaVar ? gunler[g].molaBaslangic : null, molaBitis: gunler[g].molaVar ? gunler[g].molaBitis : null,
                })),
            });
            setMesaj({ tur: 'ok', metin: kendisi ? 'Kaydedildi. Değişiklik üst yöneticinize bildirildi.' : 'Kaydedildi.' });
            randevuTanimlariGetir(true).catch(() => { });
            randevuDegisti();
            onKaydedildi?.();
            yukle();
        } catch (e) { setMesaj({ tur: 'hata', metin: hataMesaji(e) }); }
        finally { setKaydediliyor(false); }
    };

    const blokeEkle = async () => {
        const bas = blokeTur === 'IZIN' ? `${bTarih1}T00:00:00` : `${bTarih1}T${bSaat1}:00`;
        const bit = blokeTur === 'IZIN'
            ? (() => { const d = new Date(`${bTarih2 || bTarih1}T00:00:00`); d.setDate(d.getDate() + 1); return `${tarihStr(d)}T00:00:00`; })()
            : `${bTarih1}T${bSaat2}:00`;
        try {
            const r = await axiosInstance.post(`/api/Randevular/musaitlik/${kullaniciID}/bloke`, { baslangic: bas, bitis: bit, tur: blokeTur, aciklama: bAciklama || null });
            setMesaj({ tur: r.data?.uyari ? 'hata' : 'ok', metin: r.data?.uyari ?? (blokeTur === 'IZIN' ? 'İzin eklendi.' : 'Bloke saat eklendi.') });
            setBAciklama('');
            randevuDegisti();
            yukle();
        } catch (e) { setMesaj({ tur: 'hata', metin: hataMesaji(e) }); }
    };

    const blokeSil = async (id: number) => {
        if (!window.confirm('Kaldırılsın mı?')) return;
        try { await axiosInstance.post(`/api/Randevular/bloke/delete/${id}`); randevuDegisti(); yukle(); }
        catch (e) { setMesaj({ tur: 'hata', metin: hataMesaji(e) }); }
    };

    const onlineLink = `${window.location.origin}/randevu-al/${onlineToken}`;
    const tokenYenile = async () => {
        if (!window.confirm('Bağlantı yenilensin mi? Eski bağlantı çalışmaz hale gelir.')) return;
        try { const r = await axiosInstance.post(`/api/Randevular/musaitlik/${kullaniciID}/token-yenile`, { siteUrl: window.location.origin }); setOnlineToken(r.data.onlineToken); }
        catch (e) { setMesaj({ tur: 'hata', metin: hataMesaji(e) }); }
    };

    if (yukleniyor) return <div style={{ textAlign: 'center', padding: 30, color: '#9ca3af' }}><i className="fas fa-spinner fa-spin"></i></div>;

    const blokeEtiket = (b: Bloke) => {
        const s = tarihOku(b.baslangic), e = tarihOku(b.bitis);
        if (b.tur === 'IZIN') {
            const son = new Date(e.getTime() - 1);
            return tarihStr(s) === tarihStr(son) ? s.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', weekday: 'short' })
                : `${s.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })} – ${son.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' })}`;
        }
        return `${s.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', weekday: 'short' })} ${s.toTimeString().slice(0, 5)}–${e.toTimeString().slice(0, 5)}`;
    };

    return (
        <div>
            {mesaj && <div className={mesaj.tur === 'hata' ? 'tkp-hata' : ''} style={mesaj.tur === 'ok' ? { background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#047857', borderRadius: 8, padding: '8px 10px', fontSize: 12.5, marginBottom: 10 } : undefined}>{mesaj.metin}</div>}

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, fontWeight: 600, cursor: 'pointer', marginBottom: 10 }}>
                <input type="checkbox" checked={randevuAlabilir} onChange={e => setRandevuAlabilir(e.target.checked)} style={{ accentColor: '#e67d21', width: 16, height: 16 }} />
                Randevu alabilir
            </label>

            <div style={{ opacity: randevuAlabilir ? 1 : .5, pointerEvents: randevuAlabilir ? 'auto' : 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
                    <span className="tkp-etiket" style={{ fontSize: 11.5, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>Randevu günleri ve saatleri</span>
                    <button type="button" className="tkp-btn kucuk" style={{ marginLeft: 'auto' }} onClick={haftaIci}>Hafta içi 09–18</button>
                    <button type="button" className="tkp-btn kucuk" onClick={ilkGunuKopyala} title="İlk açık günün saatlerini diğer açık günlere uygula">Saatleri eşitle</button>
                </div>
                {GUN_SIRASI.map(g => {
                    const s = gunler[g] ?? bosGun();
                    return (
                        <div key={g} className="rnd-mus-satir">
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                                <input type="checkbox" checked={s.acik} onChange={e => gunGuncelle(g, { acik: e.target.checked })} style={{ accentColor: '#e67d21' }} />
                                {GUN_ADLARI[g]}
                            </label>
                            <div className="saatler">
                                {s.acik ? (
                                    <>
                                        <input type="time" value={s.baslangic} step={900} onChange={e => gunGuncelle(g, { baslangic: e.target.value })} />–
                                        <input type="time" value={s.bitis} step={900} onChange={e => gunGuncelle(g, { bitis: e.target.value })} />
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 8, cursor: 'pointer' }}>
                                            <input type="checkbox" checked={s.molaVar} onChange={e => gunGuncelle(g, { molaVar: e.target.checked })} />Mola
                                        </label>
                                        {s.molaVar && (
                                            <>
                                                <input type="time" value={s.molaBaslangic} step={900} onChange={e => gunGuncelle(g, { molaBaslangic: e.target.value })} />–
                                                <input type="time" value={s.molaBitis} step={900} onChange={e => gunGuncelle(g, { molaBitis: e.target.value })} />
                                            </>
                                        )}
                                    </>
                                ) : <span>Randevu yok</span>}
                            </div>
                        </div>
                    );
                })}
                <div className="tkp-ipucu" style={{ marginTop: 6 }}>Hiç gün seçilmezse saat kısıtı uygulanmaz (yalnızca çakışma kontrolü yapılır); online randevu ve personel önerisi için gün tanımı gerekir.</div>

                <div className="tkp-satir" style={{ marginTop: 12 }}>
                    <div className="tkp-alan">
                        <label className="tkp-etiket">Randevu aralığı</label>
                        <select className="tkp-girdi" value={aralik} onChange={e => setAralik(Number(e.target.value))}>
                            {[10, 15, 20, 30, 45, 60, 90].map(n => <option key={n} value={n}>{n} dakikada bir</option>)}
                        </select>
                    </div>
                    <div className="tkp-alan">
                        <label className="tkp-etiket">Online randevu</label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', padding: '7px 0' }}>
                            <input type="checkbox" checked={onlineAcik} onChange={e => setOnlineAcik(e.target.checked)} style={{ accentColor: '#e67d21' }} />
                            Müşteriler boş saatlerimden online randevu alabilsin
                        </label>
                    </div>
                </div>
                {onlineAcik && (
                    <div style={{ background: '#f8fafc', borderRadius: 8, padding: '8px 10px', fontSize: 12.5, marginBottom: 8 }}>
                        {!sistemOnline && <div style={{ color: '#b45309', marginBottom: 4 }}><i className="fas fa-exclamation-triangle" style={{ marginRight: 4 }}></i>Online randevu sistem genelinde kapalı (Randevular › Ayarlar).</div>}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <code style={{ flex: 1, minWidth: 200, wordBreak: 'break-all' }}>{onlineLink}</code>
                            <button type="button" className="tkp-btn kucuk" onClick={() => navigator.clipboard?.writeText(onlineLink).then(() => setMesaj({ tur: 'ok', metin: 'Bağlantı kopyalandı.' }))}><i className="fas fa-copy"></i>Kopyala</button>
                            <button type="button" className="tkp-btn kucuk" onClick={tokenYenile}><i className="fas fa-sync"></i>Yenile</button>
                        </div>
                    </div>
                )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
                {guncelleme && <span className="tkp-ipucu">Son güncelleme: {guncelleme}</span>}
                <button type="button" className="tkp-btn ana" style={{ marginLeft: 'auto' }} onClick={kaydet} disabled={kaydediliyor}>
                    <i className={`fas ${kaydediliyor ? 'fa-spinner fa-spin' : 'fa-check'}`}></i>Kaydet
                </button>
            </div>

            <div className="tkp-bolum">
                <div className="tkp-bolum-bas"><i className="fas fa-ban"></i>İzin günleri ve bloke saatler</div>
                {blokeler.length === 0 && <div className="tkp-bos">Kayıt yok.</div>}
                {blokeler.map(b => (
                    <div key={b.blokeID} className="tkp-dosya">
                        <span className={`tkp-rozet ${b.tur === 'IZIN' ? 'geciken' : 'bugun'}`}>{b.tur === 'IZIN' ? 'İzin' : 'Bloke'}</span>
                        <span className="ad" style={{ cursor: 'default' }}>{blokeEtiket(b)}{b.aciklama ? ` — ${b.aciklama}` : ''}</span>
                        <button type="button" className="tkp-ikon-btn" onClick={() => blokeSil(b.blokeID)} title="Kaldır"><i className="fas fa-trash"></i></button>
                    </div>
                ))}
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 8 }}>
                    <div className="rnd-grup">
                        <button type="button" className={blokeTur === 'IZIN' ? 'aktif' : ''} onClick={() => setBlokeTur('IZIN')}>İzin günü</button>
                        <button type="button" className={blokeTur === 'BLOKE' ? 'aktif' : ''} onClick={() => setBlokeTur('BLOKE')}>Bloke saat</button>
                    </div>
                    <input type="date" className="tkp-girdi" style={{ width: 'auto' }} value={bTarih1} onChange={e => { setBTarih1(e.target.value); if (bTarih2 < e.target.value) setBTarih2(e.target.value); }} />
                    {blokeTur === 'IZIN'
                        ? <>–<input type="date" className="tkp-girdi" style={{ width: 'auto' }} value={bTarih2} min={bTarih1} onChange={e => setBTarih2(e.target.value)} /></>
                        : <><input type="time" className="tkp-girdi" style={{ width: 'auto' }} value={bSaat1} onChange={e => setBSaat1(e.target.value)} />–<input type="time" className="tkp-girdi" style={{ width: 'auto' }} value={bSaat2} onChange={e => setBSaat2(e.target.value)} /></>}
                    <input className="tkp-girdi" style={{ flex: 1, minWidth: 140 }} value={bAciklama} onChange={e => setBAciklama(e.target.value)} placeholder="Açıklama (yıllık izin, toplantı…)" />
                    <button type="button" className="tkp-btn turuncu" onClick={blokeEkle}><i className="fas fa-plus"></i>Ekle</button>
                </div>
            </div>
        </div>
    );
};

export default MusaitlikAyarlari;
